/**
 * The other fifteen cats, as scene objects.
 *
 * Each remote slot gets an INDEPENDENT copy of one of the five loaded base
 * cats: its own skeleton, its own mixer, its own materials, its own morph and
 * pelt -- so fifteen strangers' cats can all walk differently, be shaped
 * differently and wear different coats at once.  What is shared is exactly
 * what is safe to share: geometry (skinning reads the skeleton, not the
 * mesh), textures (the pelt recolours in the shader, never touching texels),
 * animation clips (keyframe data; each mixer binds its own copy of the
 * bindings), and the compiled shader programs (CatPelt pins one cache key per
 * role, so sixteen patched coats are one program with sixteen uniform sets).
 *
 * TWO CLONING TRAPS, both verified against three r185's source:
 *
 *   - A plain Object3D.clone() copies a SkinnedMesh but keeps the SOURCE's
 *     skeleton reference (SkinnedMesh.copy shares `this.skeleton = source
 *     .skeleton`), so every "copy" is skinned by whichever mixer wrote the
 *     shared bones last -- fifteen cats striking the local cat's pose in
 *     fifteen places.  SkeletonUtils.clone() exists for exactly this: it
 *     clones the bone hierarchy and rebinds each mesh to the CLONED bones.
 *
 *   - SkeletonUtils.clone() reuses materials BY REFERENCE (its own doc says
 *     so).  Left alone, one cat's CatPelt would patch the material every cat
 *     is rendering with, and the last look applied would repaint the street.
 *     So every remote cat re-clones its materials (deduplicated within the
 *     rig, so a material two meshes share stays one instance and is patched
 *     once).  Material.clone() does NOT carry onBeforeCompile or
 *     customProgramCacheKey (checked: Material.copy's property list), so a
 *     fresh clone is genuinely unpatched.
 *
 * THE PRISTINE STOCK.  Clones are not taken from the live bases but from
 * per-base template clones captured in the constructor.  The live bases are
 * the LOCAL player's wardrobe: the customizer writes morph scales into their
 * bones and colour state into their materials (Material.clone faithfully
 * copies a recoloured `color` and emissive plumbing even though it drops the
 * shader patch), and a clone taken mid-look would bake the local player's
 * chonk into a stranger's cat -- worse, the remote CatMorph would capture
 * those scales as its rest basis and COMPOUND its own look on top.  Captured
 * once, before any look has been applied, the templates are immune to
 * whatever the customizer does later.  This is why main.js must construct
 * Net before it constructs the Customizer; the wiring notes say so.
 */
import * as THREE from "three";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import { Cat } from "../cat.js";
import { CatMorph } from "../customizer/morph.js";
import { CatPelt } from "../customizer/pelt.js";
import { BASES, normalize } from "@catworld/shared";
import { Flag, MAX_CATS, NAME_MAX } from "@catworld/shared";

/** Label plate height in metres, and where it floats: a cat is 0.32 m at the
    shoulder and the tail flicks to ~0.5, so 0.62 clears every animation
    without drifting into "signpost" territory. */
const LABEL_H = 0.085;
const LABEL_Y = 0.62;
/** The purr mark sits a little above the name. */
const PURR_H = 0.07;
const PURR_Y = 0.78;
/** Purr pulse: ~1.6 Hz and +/-10% -- a soft throb, not an alarm. */
const PURR_HZ = 1.6;
const PURR_AMP = 0.1;

/** How fast the smoothed ground speed chases the sampled one.  The wire
    rounds positions to the centimetre, and 1 cm across a 50 ms step is
    0.2 m/s of invented speed -- straight into rateFor that is a walk cycle
    visibly hunting.  120 ms of smoothing sits the rate on the true speed
    while still following a real gait change inside a couple of snapshots. */
const SPEED_TAU = 0.12;

/**
 * A remote cat: a Cat in every behavioural respect, built on a skeleton
 * clone instead of a loaded GLTF.
 *
 * The measurement overrides are the whole reason this subclass exists.
 * Cat's constructor plays the land and jump clips through the mixer, 25
 * sampled frames each with a full-scene updateMatrixWorld, to find the
 * crouch and stretch poses -- worth it once per base at boot, and pure waste
 * per remote spawn: a clone of sandy IS sandy, bone for bone and clip for
 * clip, so the base's measured tables are copied across instead and the
 * overrides turn the constructor's measuring passes into no-ops (they run
 * from inside super(), so they only plant placeholders the copy replaces).
 * That keeps a cat joining mid-game a millisecond-scale event instead of a
 * visible hitch.
 */
class RemoteCat extends Cat {
  constructor(stock) {
    super({ scene: cloneSkinned(stock.template), animations: stock.clips }, stock.base.meta);
    this._crouchT = stock.base._crouchT;
    this._crouchD = stock.base._crouchD;
    this._stretchT = stock.base._stretchT;
    // Own materials, per the header.  Before the morph and pelt are built,
    // so the pelt patches THESE.
    this.ownMaterials = adoptMaterials(this.root);
  }

  _measureCrouch() { this._crouchT = [0, 0]; this._crouchD = [1, 0]; }
  _measureStretch() { this._stretchT = 0; }
}

/** Replace every mesh material with a private clone, deduplicated so a
    material shared by two meshes stays shared WITHIN this cat.  Returns the
    clones, for dispose(). */
function adoptMaterials(root) {
  const map = new Map();
  const owned = [];
  const remap = (m) => {
    let n = map.get(m);
    if (!n) {
      n = m.clone();
      map.set(m, n);
      owned.push(n);
    }
    return n;
  };
  root.traverse((o) => {
    if (!o.isMesh) return;
    o.material = Array.isArray(o.material) ? o.material.map(remap) : remap(o.material);
  });
  return owned;
}

/**
 * A name, as a canvas texture.  One texture per cat, regenerated only when
 * the name actually changes -- never a DOM element per cat (fifteen absolutely
 * positioned divs re-projected every frame is layout work the compositor
 * charges for even when nothing moved) and never a per-frame redraw.
 */
function makeNameTexture(name) {
  if (typeof document === "undefined") return null;   // headless tools
  const c = document.createElement("canvas");
  const g = c.getContext("2d");
  const font = '600 30px "Archivo", system-ui, sans-serif';   // the HUD's face
  g.font = font;
  const textW = Math.ceil(g.measureText(name).width);
  const padX = 16, h = 46;
  c.width = textW + padX * 2;
  c.height = h;
  // The HUD panel colours, so the plates read as part of the same interface.
  g.fillStyle = "rgba(20,21,18,0.72)";
  roundRect(g, 0, 0, c.width, h, 9);
  g.fill();
  g.font = font;
  g.fillStyle = "#f1efe8";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(name, c.width / 2, h / 2 + 1);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;         // the plate is usually viewed at a rake
  return tex;
}

function roundRect(g, x, y, w, h, r) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/** The purr mark -- one heart texture and one material, shared by every cat
    that ever purrs.  Per-cat all that varies is a Sprite's visibility and
    scale, which is free. */
let purrMaterial = null;
function getPurrMaterial() {
  if (purrMaterial || typeof document === "undefined") return purrMaterial;
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d");
  g.font = "48px system-ui, sans-serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  // The HUD accent: purring is the game's "it is working" signal, and amber
  // is what this interface already says that in.
  g.fillStyle = "#e2a73c";
  g.fillText("♥", 32, 36);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  purrMaterial = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
  return purrMaterial;
}

export class Roster {
  /**
   * @param bases  the loaded Cat instances, index-aligned with schema.js's
   *               BASES (and therefore main.js's CATS).
   * @param scene  where remote cats live.
   */
  constructor({ bases, scene }) {
    this.scene = scene;
    this.mySlot = -1;
    this.slots = new Array(MAX_CATS).fill(null);
    // The pristine stock -- see the header.  Taken NOW, which is why Net
    // wants constructing before the customizer gets a chance to dress the
    // bases.  main.js as wired starts the customizer a few statements
    // earlier, so a returning player's restored look may ALREADY be on the
    // active base -- the scrub below exists for exactly that window.
    this._stock = (bases || []).map((base) => {
      if (!base) return null;
      const template = cloneSkinned(base.root);
      template.visible = true;          // four of the five bases are hidden
      // A base that is already wearing a look has had morph scales written
      // into its bones, and a clone taken now inherits them -- the remote
      // CatMorph would then capture them as its rest basis and COMPOUND a
      // stranger's chonk on top of the local player's.  The base's own
      // CatMorph captured every bone channel at ITS construction (from the
      // pristine load: only morphs ever write scales), so that capture IS
      // the rest pose, mapped onto the clone by bone name -- names are
      // unique within both rig families, per the clips.json bone lists.
      if (base.morph) {
        const rest = new Map();
        for (const r of base.morph._rest) rest.set(r.bone.name, r);
        template.traverse((o) => {
          const r = o.isBone ? rest.get(o.name) : null;
          if (r) {
            o.position.copy(r.p);
            o.quaternion.copy(r.q);
            o.scale.copy(r.s);
          }
        });
        template.scale.copy(base.morph._scale0);
      }
      adoptMaterials(template);         // template materials pre-date any pelt patch
      return {
        base,
        template,
        // Clips are keyframe data bound by bone NAME at clipAction time, so
        // one array serves every clone of this rig.
        clips: Object.values(base.actions).map((a) => a.getClip()),
      };
    });
    /** Bumped whenever a sampled HUMAN flag flips, so the facade knows the
        roster listing it last emitted has gone stale without either side
        polling every entry every frame. */
    this.humanRev = 0;
  }

  /** How many remote cats are currently on the street. */
  get count() {
    let n = 0;
    for (const e of this.slots) if (e) n++;
    return n;
  }

  /**
   * A slot's record arrived, or changed.  One entry point for "cat joined",
   * "cat renamed" and "cat rebuilt in the customizer", because the protocol
   * sends one message for all three and replacing what we hold is correct
   * for each.
   */
  /**
   * Adopt the slot the server says is ours, and drop any twin already built
   * for it.
   *
   * The guard in setRecord is not enough on its own, because the ORDER is not
   * ours to choose: the server broadcasts the new cat to every socket -- this
   * one included -- and only then answers the joiner with `welcome`.  For the
   * length of that gap `mySlot` is still -1, the broadcast looks like any
   * other stranger arriving, and a full remote rig is built for the player's
   * own cat.  It then stands exactly where the player stands, wearing the
   * player's look and a name plate, doubling every silhouette in every
   * screenshot until the tab is reloaded.
   *
   * Setting the slot has to undo that, so the two live in one method rather
   * than as a bare field somebody has to remember to clean up after.
   */
  claimOwn(slot) {
    this.mySlot = slot;
    if (Number.isInteger(slot) && slot >= 0) this.remove(slot);
  }

  setRecord(slot, rec) {
    if (!Number.isInteger(slot) || slot < 0 || slot >= MAX_CATS) return;
    if (slot === this.mySlot) return;   // my own cat is the player's, not ours
    const r = rec && typeof rec === "object" ? rec : {};
    // The look is server-relayed CLIENT input -- some other person's
    // customizer -- so unlike every other server field it goes through the
    // schema's own normalize, exactly as protocol.js prescribes: a hostile
    // 1e9 in `size` must become a clamped slider value before it becomes a
    // bone scale.
    const look = normalize(r.look);
    const name = String(r.name ?? "").replace(/\s+/g, " ").trim().slice(0, NAME_MAX) || "Cat";
    let baseIndex = BASES.indexOf(look.base);
    if (baseIndex < 0) baseIndex = 0;
    if (!this._stock[baseIndex]) return;   // that base failed to load; no rig to wear

    let e = this.slots[slot];
    if (e && e.baseIndex !== baseIndex) {
      // A different rig is a different skeleton and different materials:
      // rebuilding in place would mean re-reading rest state out of bones a
      // mixer has already moved (morph.js's warning).  Tear down, respawn.
      this.remove(slot);
      e = null;
    }
    if (!e) {
      e = this._spawn(slot, baseIndex);
      this.slots[slot] = e;
    }

    e.look = look;
    e.morph.apply(look);
    e.pelt.apply(look);
    this._fitOverlays(e);

    if (name !== e.name) {
      e.name = name;
      this._setLabel(e, name);
    }
  }

  _spawn(slot, baseIndex) {
    const stock = this._stock[baseIndex];
    const cat = new RemoteCat(stock);
    // Always a morph and a pelt, where the customizer builds them lazily and
    // skips default looks.  Two reasons the economy does not transfer: a
    // remote look is usually NOT default (someone bothered to join with it),
    // and the pelt patch is what guarantees this cat's materials answer to
    // this cat's uniforms alone -- an unpatched material is only safe when
    // nothing else patched ones like it, which stops being true the moment
    // the local player opens the builder.  The recompile the customizer is
    // avoiding happens once per ROLE here, not once per cat: every coat
    // patch shares one pinned program cache key.
    const morph = new CatMorph(cat.root);
    const pelt = new CatPelt(cat.root);
    cat.morph = morph;                  // cat.update() re-applies it post-mixer

    // Hidden until the first sample lands: a cat standing at the world
    // origin for the half-second between `cat` and its first snapshot row
    // reads as a spawn bug, and the origin is the middle of the front walk.
    cat.root.visible = false;
    this.scene.add(cat.root);

    const e = {
      cat, morph, pelt, baseIndex,
      look: null, name: null,
      label: null, labelTex: null, purr: null,
      // Reused sample target: fifteen cats at display rate must not allocate.
      out: { x: 0, y: 0, z: 0, facing: 0, state: "idle", charge: 0, happy: 0, flags: 0, speed: 0 },
      placed: false,
      speed: 0,
      purrPhase: 0,
      /** Somebody at the wheel, off the snapshot HUMAN flag; null until the
          first sample says either way. */
      human: null,
    };

    const pm = getPurrMaterial();
    if (pm) {
      const s = new THREE.Sprite(pm);
      s.visible = false;
      cat.root.add(s);
      e.purr = s;
    }
    return e;
  }

  _setLabel(e, name) {
    const tex = makeNameTexture(name);
    if (!tex) return;
    if (e.label) {
      // Same sprite, new plate: only the texture is per-name.
      e.labelTex?.dispose();
      e.label.material.map = tex;
      e.label.material.needsUpdate = true;
    } else {
      const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
      e.label = new THREE.Sprite(mat);
      e.cat.root.add(e.label);
    }
    e.labelTex = tex;
    this._fitOverlays(e);
  }

  /**
   * Seat the label and purr mark for this cat's current look.  They are
   * children of the root so they cost no per-frame bookkeeping, but the root
   * carries the look's `size` as a uniform scale -- undivided, a 140% cat
   * would wear a 140% billboard.  Everything here is divided back out, so
   * every plate on the street renders the same physical size.
   */
  _fitOverlays(e) {
    const s = e.look ? e.look.size : 1;
    if (e.label) {
      const img = e.labelTex.image;
      const aspect = img && img.height ? img.width / img.height : 4;
      e.label.position.set(0, LABEL_Y / s, 0);
      e.label.scale.set((aspect * LABEL_H) / s, LABEL_H / s, 1);
    }
    if (e.purr) {
      e.purr.position.set(0, PURR_Y / s, 0);
      e.purrScale = PURR_H / s;
      e.purr.scale.set(e.purrScale, e.purrScale, 1);
    }
  }

  /** A slot emptied.  Clean enough to run sixteen thousand times: only what
      this module created is disposed -- geometry and coat textures belong to
      the base GLBs and outlive every visitor. */
  remove(slot) {
    const e = this.slots[slot];
    if (!e) return;
    this.slots[slot] = null;
    this.scene.remove(e.cat.root);
    e.cat.mixer.stopAllAction();
    for (const m of e.cat.ownMaterials) m.dispose();   // frees program refs, not shared textures
    // The invisible per-cat texture: a skeleton uploads its bone matrices
    // through a DataTexture the renderer creates lazily, one per Skeleton --
    // and SkeletonUtils.clone gives every SkinnedMesh its own cloned
    // Skeleton, so nothing above ever shares one.  Nothing disposes it
    // either: not removing the mesh, not disposing its material.  Measured
    // in the harness churn before this block existed: 2-3 textures leaked
    // per departed cat, forever.
    const skeletons = new Set();
    e.cat.root.traverse((o) => { if (o.isSkinnedMesh) skeletons.add(o.skeleton); });
    for (const s of skeletons) s.dispose();
    if (e.label) {
      e.labelTex?.dispose();          // ours: one canvas per name
      e.label.material.dispose();
    }
    // e.purr shares the module-wide material; the sprite dies with the root.
  }

  /** Everything out -- for a full roster replacement at (re)welcome. */
  clear() {
    for (let i = 0; i < MAX_CATS; i++) this.remove(i);
  }

  /**
   * Per frame: sample every live slot at the render clock, pose it, choose
   * its clip by the SAME rules main.js applies to the local cat (the fade
   * table is copied verbatim from its frame loop -- a remote pounce release
   * must read as instantaneous for the same reason a local one does), and
   * advance its mixer.
   */
  update(dt, client) {
    for (let slot = 0; slot < MAX_CATS; slot++) {
      const e = this.slots[slot];
      if (!e) continue;
      const s = client ? client.sampleLive(slot, e.out) : null;
      if (s) {
        if (!e.placed) {
          e.placed = true;
          e.cat.root.visible = true;
          e.speed = s.speed;
        }
        const root = e.cat.root;
        root.position.set(s.x, s.y, s.z);
        root.rotation.y = s.facing;

        e.speed += (s.speed - e.speed) * (1 - Math.exp(-dt / SPEED_TAU));
        const st = s.state;
        const cat = e.cat;
        const rate = st === "walk" || st === "run" || st === "stalk"
          ? cat.rateFor(st, e.speed) : 1;
        // main.js's fade table, transition for transition.
        const fade = st === "land" ? 0.06
          : st === "pounce" ? 0.10
          : st === "cling" ? 0.08
          : cat.current === "pounce" ? 0.04
          : 0.15;
        // play() ignores a state this base has no clip for (the generated
        // cats lack stalk and swipe), same as main.js gating its input --
        // the cat holds its previous clip through the moment instead.
        cat.play(st, { rate, fade, charge: s.charge });

        // The HUMAN flag lives in the snapshot row, not the record, so the
        // roster listing (which is record-driven) learns it here.  The rev
        // bump is what tells the facade to re-announce -- a cat's brain
        // taking over when its person closes the tab is roster news.
        const human = (s.flags & Flag.HUMAN) !== 0;
        if (human !== e.human) {
          e.human = human;
          this.humanRev++;
        }

        if (e.purr) {
          const purring = (s.flags & Flag.PURR) !== 0;
          e.purr.visible = purring;
          if (purring) {
            e.purrPhase += dt * PURR_HZ * 2 * Math.PI;
            const k = 1 + Math.sin(e.purrPhase) * PURR_AMP;
            e.purr.scale.set(e.purrScale * k, e.purrScale * k, 1);
          }
        }
      }
      // No sample -- before the first snapshot, or the slot dropped out of
      // the rows mid-buffer -- and the cat simply stays where it last stood,
      // clip still looping.  A breathing cat holding its ground is honest; a
      // frozen one, or one lunging along a guessed velocity, is not.
      if (e.placed) e.cat.update(dt);
    }
  }
}
